/* eslint-disable import/prefer-default-export */
import InsightViewOption from 'enums/InsightViewOption'
import { InsightCount } from 'schemas/action/InsightCountSchema'
import { DashboardWidget } from 'features/widget/types'
import { InsightCategoryGroup } from 'schemas/action/InsightCategoryGroupSchema'
import { CategoryStrings } from 'enums/Category'
import { InsightData } from 'schemas/action/InsightDataSchema'
import { UsageAndAttitudeWidgetType } from '../types'

type FoamTreeData = {
  label?: string
  firstLevel?: FoamTreeData
  secondLevel?: FoamTreeData
  insightType?: CategoryStrings
  weight?: number
  groups?: FoamTreeData[]
}

const processSecondLayer = (
  insights: InsightCategoryGroup,
  labelAName: string,
  insightA: InsightData,
  insightType: string,
  secondLayerMaxDisplay: number
) =>
  insights?.dataset
    ?.filter((insight) => insight.referenceKey === labelAName)
    ?.flatMap((insight) =>
      insight?.set?.slice(0, secondLayerMaxDisplay)?.map((entry) => ({
        label: entry.name,
        firstLevel: insightA,
        secondLevel: entry,
        insightType,
        weight: Math.ceil(Math.sqrt(entry.value!))
      }))
    )

const processFirstLayer = (
  insights: InsightCategoryGroup,
  firstLayerMaxDisplay: number,
  insightType: string,
  secondLayerMaxDisplay: number,
  widgetSetting: UsageAndAttitudeWidgetType,
  catLg: number,
  groupsA: FoamTreeData[]
) =>
  insights?.dataset?.[0]?.set
    ?.slice(0, firstLayerMaxDisplay)
    ?.map((insightA) => {
      const labelAName = insightA.name!
      const groupsB = processSecondLayer(
        insights,
        labelAName,
        insightA,
        insightType,
        secondLayerMaxDisplay
      ) as FoamTreeData[]

      const levelA = {
        label: labelAName!,
        firstLevel: insightA,
        secondLevel: undefined,
        insightType: insightType!,
        weight: Math.ceil(Math.sqrt(insightA.value!)),
        groups: groupsB
      }

      if (
        widgetSetting.viewOption === InsightViewOption.EMOTIONS_BEHAVIORS_TO_THINGS ||
        catLg === 0
      ) {
        return levelA
      }

      const existingGroup = groupsA.find((group) => group.label === labelAName)
      if (existingGroup) {
        existingGroup.groups = [...(existingGroup.groups || []), ...groupsB]
        return null
      }

      return levelA
    })
    .filter(Boolean)

const filterInsightData = (
  insightData?: InsightCategoryGroup[],
  minCount = 0
): InsightCategoryGroup[] => {
  if (!insightData) {
    return []
  }

  return insightData?.map((categoryGroup) => ({
    ...categoryGroup,
    dataset: categoryGroup.dataset?.map((labeledInsight) => ({
      ...labeledInsight,
      set: labeledInsight.set?.filter((entry) => entry.value! >= minCount)
    }))
  }))
}

export const convertToFoamTreeData = (
  widgetData: DashboardWidget<InsightCount>,
  widgetSetting: UsageAndAttitudeWidgetType
) => {
  const insightData = filterInsightData(
    widgetData?.data?.[0]?.results?.[0]?.data.insights,
    widgetSetting.minCount!
  ) as InsightCategoryGroup[]

  if (!insightData) {
    return []
  }

  const secondLayerMaxDisplay = widgetSetting.topNSecond
  const groupsA: FoamTreeData[] = []

  insightData.forEach((insights: InsightCategoryGroup, catLg) => {
    const firstLayerMaxDisplay = Math.min(
      widgetSetting.topN!,
      insights?.dataset?.[0]?.set?.length || Infinity
    )
    const insightType = insights?.dataset?.[0]?.insightType

    const firstLayerGroups = (processFirstLayer(
      insights,
      firstLayerMaxDisplay,
      insightType!,
      secondLayerMaxDisplay!,
      widgetSetting,
      catLg,
      groupsA
    ) || []) as FoamTreeData[]

    groupsA.push(...firstLayerGroups)
  })
  return groupsA
}
